/* Scoped to the tagged main product form; standard product and cart scripts remain unchanged. */
if (!customElements.get('npl-accessory-picker')) {
  customElements.define('npl-accessory-picker', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = document.getElementById(this.dataset.form) || this.closest('form');
      if (!this.form) return;
      this.section = this.closest('section');
      this.productForm = this.form.closest('product-form');
      this.submit = this.form.querySelector('[name="add"]');
      this.variantInput = this.form.querySelector('[name="id"]');
      this.status = this.querySelector('[data-npl-status]');
      this.hint = this.querySelector('[data-npl-hint]');
      this.retry = this.querySelector('[data-npl-retry]');
      this.types = [...this.querySelectorAll('[data-npl-type]')];
      this.groups = [...this.querySelectorAll('[data-npl-sizes]')];
      this.sequence = 0;
      this.loadedId = String(this.dataset.initialVariant);
      this.serverAvailable = this.submit.dataset.nplAvailable === 'true';
      this.onSubmit = this.validateSubmit.bind(this);
      this.form.addEventListener('submit', this.onSubmit, true);
      this.hidden = false;
      try {
        this.variants = JSON.parse(this.querySelector('[data-npl-variants]').textContent);
        const options = JSON.parse(this.querySelector('[data-npl-options]').textContent);
        if (options.length !== 2 || options[0] !== '種類' || options[1] !== 'サイズ' ||
            !this.variants.some(v => v.option1 === 'スペシャルセット' && v.option2 === 'セット')) {
          throw new Error('Expected 種類 / サイズ options and スペシャルセット / セット variant.');
        }
      } catch (error) {
        this.configError = true;
        this.setButton(false, '現在購入できません');
        this.status.textContent = 'この商品は現在購入できません。ショップへお問い合わせください。';
        this.querySelector('[data-npl-types]').hidden = true;
        console.error('4npL accessory product configuration:', error);
        return;
      }
      const initial = this.variants.find(v => String(v.id) === this.loadedId);
      const initialType = this.types.find(input => input.value === initial?.option1) || this.types[0];
      initialType.checked = true;
      // Restore a single-item size only for an explicit variant URL. Sets always require three choices.
      if (initial && this.dataset.selectedVariant && initial.option1 !== 'スペシャルセット') {
        const group = this.groups.find(item => item.dataset.nplSizes === initial.option1);
        const size = group && [...group.querySelectorAll('[data-npl-size]')].find(input => input.value === initial.option2);
        if (size) size.checked = true;
      }
      this.onChange = event => {
        if (event.target.matches('[data-npl-type], [data-npl-size]')) this.sync();
      };
      this.onRetry = () => { this.loadedId = ''; this.sync(); };
      this.addEventListener('change', this.onChange);
      this.retry.addEventListener('click', this.onRetry);
      this.sync();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.form?.removeEventListener('submit', this.onSubmit, true);
      this.removeEventListener('change', this.onChange);
      this.retry?.removeEventListener('click', this.onRetry);
      this.initialized = false;
    }

    selectedType() { return this.types.find(input => input.checked)?.value; }
    sizeFor(type) {
      return this.groups.find(group => group.dataset.nplSizes === type)?.querySelector('[data-npl-size]:checked')?.value;
    }
    variantFor(type, size) { return this.variants.find(v => v.option1 === type && v.option2 === size); }

    updateGroups() {
      const type = this.selectedType();
      const fullSet = type === 'スペシャルセット';
      this.groups.forEach(group => {
        const groupType = group.dataset.nplSizes;
        group.hidden = !fullSet && groupType !== type;
        group.querySelectorAll('[data-npl-size]').forEach(input => {
          const variant = this.variantFor(groupType, input.value);
          // Set sizes deliberately ignore single-item inventory, including missing/sold-out singles.
          input.disabled = !fullSet && (group.hidden || !variant?.available);
          if (!fullSet && !group.hidden && input.disabled) input.checked = false;
          input.nextElementSibling.querySelector('.npl-picker__unavailable').hidden = fullSet || !!variant?.available;
        });
        const property = group.querySelector('[data-npl-property]');
        property.disabled = !fullSet;
        property.value = this.sizeFor(groupType) || '';
      });
      this.types.forEach(input => {
        input.disabled = !this.variants.some(v => v.option1 === input.value &&
          (input.value !== 'スペシャルセット' || v.option2 === 'セット'));
      });
      this.complete = fullSet ? this.groups.every(group => !!this.sizeFor(group.dataset.nplSizes)) : !!this.sizeFor(type);
      this.hint.textContent = fullSet
        ? 'リング・ブレスレット・ネックレスのサイズをそれぞれ選択してください。'
        : `${type}のサイズを選択してください。`;
    }

    setButton(enabled, text) {
      this.submit.disabled = !enabled;
      this.submit.querySelector('span').textContent = text;
    }

    updateButton() {
      const available = this.currentVariant?.available && this.serverAvailable;
      this.ready = !!(available && this.complete && this.loadedId === String(this.currentVariant.id));
      this.setButton(this.ready, !available ? (window.variantStrings?.soldOut || '売り切れ')
        : this.complete ? (window.variantStrings?.addToCart || 'カートに追加する') : 'サイズを選択してください');
    }

    async sync() {
      if (this.configError) return;
      const request = ++this.sequence;
      this.abort?.abort();
      this.retry.hidden = true;
      this.status.textContent = '';
      this.productForm?.handleErrorMessage();
      this.updateGroups();
      const type = this.selectedType();
      this.currentVariant = type === 'スペシャルセット' ? this.variantFor(type, 'セット')
        : this.variantFor(type, this.sizeFor(type)) ||
          this.variants.find(v => v.option1 === type && v.available) || this.variants.find(v => v.option1 === type);
      const variant = this.currentVariant;
      this.ready = false;
      if (!variant) {
        this.variantInput.value = '';
        this.setButton(false, '現在購入できません');
        this.status.textContent = 'この種類は現在購入できません。';
        return;
      }
      this.variantInput.value = variant.id;
      this.variantInput.disabled = false;
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      const installment = this.section.querySelector('form.installment [name="id"]');
      if (installment) installment.value = variant.id;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      this.section.querySelector('share-button')?.updateUrl?.(url.href);
      if (this.loadedId === String(variant.id)) {
        this.updateButton();
        return;
      }
      this.setButton(false, '選択内容を確認中…');
      this.status.textContent = '価格・在庫を確認しています。';
      this.abort = new AbortController();
      const sectionUrl = new URL(this.dataset.url, window.location.origin);
      sectionUrl.searchParams.set('variant', variant.id);
      sectionUrl.searchParams.set('section_id', this.dataset.section);
      try {
        const response = await fetch(sectionUrl, { signal: this.abort.signal });
        if (!response.ok) throw new Error('Product section request failed.');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (request !== this.sequence || !this.isConnected) return;
        const serverButton = html.getElementById(`ProductSubmitButton-${this.dataset.section}`);
        const serverInput = html.getElementById(this.form.getAttribute('id'))?.querySelector('[name="id"]');
        if (!serverButton || String(serverInput?.value) !== String(variant.id)) throw new Error('Unexpected product section.');
        const serverPicker = html.querySelector('npl-accessory-picker');
        this.variants = JSON.parse(serverPicker.querySelector('[data-npl-variants]').textContent);
        this.currentVariant = this.variants.find(v => String(v.id) === String(variant.id));
        if (!this.currentVariant) throw new Error('Variant no longer exists.');
        this.serverAvailable = serverButton.dataset.nplAvailable === 'true';
        // Shopify renders prices, translated stock labels, sale badges, and quantity rules.
        ['price-', 'Inventory-', 'Sku-', 'Price-Per-Item-', 'Volume-', 'Volume-Note-'].forEach(prefix => {
          const id = `${prefix}${this.dataset.section}`;
          const source = html.getElementById(id);
          const destination = this.section.querySelector(`[id="${id}"]`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.className = source.className;
          }
        });
        const sourceInstallment = html.querySelector('form.installment');
        const destinationInstallment = this.section.querySelector('form.installment');
        if (sourceInstallment && destinationInstallment) destinationInstallment.innerHTML = sourceInstallment.innerHTML;
        const mediaId = this.currentVariant.featured_media?.id;
        if (mediaId) this.section.querySelector('media-gallery')?.setActiveMedia?.(`${this.dataset.section}-${mediaId}`);
        const pickup = this.section.querySelector('pickup-availability');
        if (this.serverAvailable) pickup?.fetchAvailability?.(variant.id);
        else if (pickup) { pickup.removeAttribute('available'); pickup.innerHTML = ''; }
        this.loadedId = String(variant.id);
        this.updateGroups();
        this.status.textContent = '';
        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId: this.dataset.section, html, variant: this.currentVariant } });
        }
        this.updateButton();
      } catch (error) {
        if (error.name === 'AbortError' || request !== this.sequence) return;
        this.loadedId = '';
        this.ready = false;
        this.setButton(false, '再読み込みしてください');
        this.status.textContent = '価格・在庫を確認できませんでした。再読み込みしてください。';
        this.retry.hidden = false;
      }
    }

    validateSubmit(event) {
      const fullSet = this.selectedType() === 'スペシャルセット';
      const choicesValid = fullSet ? this.groups.every(group => !!this.sizeFor(group.dataset.nplSizes))
        : !!this.variantFor(this.selectedType(), this.sizeFor(this.selectedType()))?.available;
      const quantity = this.form.elements.namedItem('quantity');
      const validQuantity = !quantity || (Number(quantity.value) > 0 && quantity.checkValidity());
      if (this.configError || !this.ready || !choicesValid || !validQuantity ||
          String(this.variantInput.value) !== String(this.currentVariant?.id)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.status.textContent = !validQuantity ? '購入可能な数量を入力してください。' : '種類と必要なサイズをすべて選択し、価格・在庫の確認をお待ちください。';
        if (!validQuantity) quantity.focus();
        else this.querySelector('fieldset:not([hidden]) input:not(:disabled)')?.focus();
      }
    }
  });
}
